import { Params } from '@angular/router';
import {
    IgAudienceCity,
    IgAudienceCountry,
    IgAudienceGenderAge,
} from '@core/models/ApiFields/ig-entities';
import { Paginated, RawDataWithBreakd, ResDataItem } from '@core/models/IApi';
import { HttpBuilder } from '../../social-shared/services/http/http-builder';
import { IG_ENDPOINT } from '../../social-listening/config/api-params';
import { AudienceState } from '../../social-shared/store/audience.store';
import {
    AUD_API_BREAKD,
    AUD_API_ENDP,
    AUD_API_FIELDS,
    AUD_API_SORT,
    AUD_ENTITIES,
    AudEntities,
    AudTimef,
} from '../config/audience.config';
import { AudTypes } from '@core/models/ApiFields/ig-field-values.types';

export class AudienceHttpBuilder extends HttpBuilder<any> {
    entitiesWithBreakd: string[] = [AUD_ENTITIES.GENDER_AGE];
    type!: AudTypes;
    entity!: AudEntities;
    timeframe?: AudTimef;

    constructor(type: AudTypes, entity: AudEntities) {
        super();
        this.type = type;
        this.entity = entity;
    }

    reqData(state: AudienceState, timef?: AudTimef) {
        this.timeframe = timef;

        const url = this.buildUrl(this.getEndpoint(this.entity));
        const params = this.buildParams();

        this.setIsLoading(state, true);
        this.makeGetReq(url, params, state);
    }

    /* REQ BUILDING */

    protected getEndpoint(entity: AudEntities) {
        return IG_ENDPOINT + AUD_API_ENDP[entity];
    }

    protected buildParams(): Params {
        return {
            ...this.getFields(this.entity),
            ...this.getBreakdowns(this.entity),
            ...this.getMode(),
            ...this.getAudienceType(this.type),
            ...this.getTimeframe(this.timeframe),
            ...this.getSort(this.entity),
            ...this.getLimit(this.entity),
            ...this.xtraFilters(),
        };
    }

    protected getFields(entity: AudEntities) {
        return {
            fields: AUD_API_FIELDS[entity].join(),
        };
    }

    protected getBreakdowns(entity: AudEntities) {
        if (this.entitiesWithBreakd.includes(this.entity))
            return {
                breakdowns: AUD_API_BREAKD[entity].join(),
            };
        else return {};
    }

    protected getMode() {
        return { mode: 1 };
    }

    protected getAudienceType(type: string) {
        return {
            [IgAudienceGenderAge.getPrefixed('type')]: 'eq:' + type,
        };
    }

    protected getTimeframe(timef?: string) {
        if (timef)
            return {
                [IgAudienceGenderAge.getPrefixed('timeframe')]: 'eq:' + timef,
            };
        else return {};
    }

    protected getSort(entity: AudEntities) {
        const field = AUD_API_SORT[entity].join();
        return field ? { sort: field + '_desc' } : {};
    }

    protected getLimit(entity: AudEntities) {
        const field = AUD_API_SORT[entity].join();
        return field ? { limit: 5 } : {};
    }

    protected xtraFilters() {
        if (this.entity === AUD_ENTITIES.COUNTRY)
            return { [IgAudienceCountry.getPrefixed('type')]: 'eq:' + this.type };
        if (this.entity === AUD_ENTITIES.CITY)
            return { [IgAudienceCity.getPrefixed('type')]: 'eq:' + this.type };
        else return {};
    }

    /* REQ MAKING */

    protected override setIsLoading(state: AudienceState, isLoading: boolean): void {
        state.isLoading[this.entity].set(isLoading);
    }

    protected override formatRes(res: Paginated<ResDataItem[]>): Record<string, any>[] {
        if (this.entitiesWithBreakd.includes(this.entity))
            return this.formatResWithBreakd(res as Paginated<RawDataWithBreakd[]>);
        else return super.formatRes(res);
    }

    protected formatResWithBreakd(res: Paginated<RawDataWithBreakd[]>) {
        const formatted = res.data.map(group => {
            const field = AUD_API_BREAKD[this.entity].join();
            const split = field.split('.');
            const breakdVal = group.breakdown[split[0]][split[1]] as string;
            return {
                [field]: breakdVal as string,
                data: group.data.map(data => this.flattenOneLevel(data)),
            };
        });
        return formatted;
    }

    protected override handleSuccess(state: any, data: any): void {
        state.audData[this.entity].set(data);
    }
}
